import { useState } from "react";
import { apiErrorText } from '../lib/apiError';
import useSWR from "swr";
import { hapticSuccess } from "../utils/haptics";
import TabPageLayout, { PageLoader, EmptyState, StatCard, DataCard } from "./TabPageLayout";
import ChipTabs from "./ui/chip-tabs";
import { LayoutActionButton } from "./Buttons";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import {
  Gift, Plus, Copy, Check, Loader2, Ban, RefreshCw, MinusCircle, PlusCircle,
  Mail, Search, Calendar, CreditCard, Download, ChevronDown, ChevronRight,
  ShoppingBag, Settings as SettingsIcon, MessageCircle, Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import apiV2 from "../utils/apiV2";

const STATUS_STYLES = {
  active: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
  depleted: "border-gray-200 bg-gray-50 text-gray-600",
  expired: "border-amber-200 bg-amber-50/60 text-amber-700",
  disabled: "border-red-200 bg-red-50/60 text-red-700",
};

const STATUS_DOT = {
  active: "bg-emerald-500",
  depleted: "bg-gray-400",
  expired: "bg-amber-500",
  disabled: "bg-red-500",
};

// Tiny flat status chip — matches the dot+thin-border style used in Webhooks
const StatusChip = ({ status, className = "" }) => (
  <span
    className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-md border capitalize ${STATUS_STYLES[status] || "border-gray-200 bg-gray-50 text-gray-600"} ${className}`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status] || "bg-gray-400"}`} />
    {status || "—"}
  </span>
);

const fmtMoney = (amount, currency = "USD") => {
  const num = Number(amount || 0);
  // Use Intl.NumberFormat for proper i18n — handles JPY zero-decimals, locale-specific
  // formatting (e.g. "1.234,56 €" in German), and unknown ISO codes gracefully.
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      currencyDisplay: "narrowSymbol",
    }).format(num);
  } catch {
    // Fallback if currency code is invalid
    return `${num.toFixed(2)} ${currency}`;
  }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const TXN_LABEL = {
  issue: { label: "Issued", color: "text-blue-600", Icon: PlusCircle },
  redeem: { label: "Redeemed", color: "text-red-600", Icon: MinusCircle },
  refund: { label: "Refunded", color: "text-emerald-600", Icon: RefreshCw },
  adjust: { label: "Adjusted", color: "text-amber-600", Icon: RefreshCw },
};

const defaultExpiry = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

const swrFetcher = (url) => apiV2.get(url).then((r) => r.data);

const GiftCardsTab = () => {
  const [showIssue, setShowIssue] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [copiedCode, setCopiedCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  // Action dialogs (replace window.prompt/confirm — production-grade UX)
  const [actionDialog, setActionDialog] = useState(null); // {type, cardId, ...}
  const [actionAmount, setActionAmount] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [disableConfirm, setDisableConfirm] = useState(null); // cardId

  // Authoritative org-wide stats (not capped by the page size)
  const { data: stats, mutate: mutateStats } = useSWR("/admin/gift-cards/stats/summary", swrFetcher, { revalidateOnFocus: false });

  // List query — paginated server-side. Filter/search changes reset to page 0.
  const params = new URLSearchParams();
  if (statusFilter) params.set("status", statusFilter);
  if (search) params.set("search", search);
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String(page * PAGE_SIZE));
  const listKey = `/admin/gift-cards?${params.toString()}`;
  const { data: listData, isLoading, mutate } = useSWR(listKey, swrFetcher);
  const cards = listData?.items || [];
  const total = listData?.total ?? 0;
  const startRow = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endRow = Math.min(total, (page + 1) * PAGE_SIZE);
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < total;

  // Reset to page 0 whenever filters change (avoids "empty page" weirdness)
  const onSearchChange = (v) => { setSearch(v); setPage(0); };
  const onStatusChange = (v) => { setStatusFilter(v); setPage(0); };

  // Detail query
  const { data: detail, mutate: mutateDetail } = useSWR(
    selectedId ? `/admin/gift-cards/${selectedId}` : null,
    swrFetcher,
  );

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Code copied");
    setTimeout(() => setCopiedCode(null), 1500);
  };

  const handleDisable = async (id) => {
    setBusy(true);
    try {
      await apiV2.post(`/admin/gift-cards/${id}/disable`);
      toast.success("Gift card disabled");
      mutate();
      mutateStats?.();
      mutateDetail?.();
      setDisableConfirm(null);
    } catch (e) {
      toast.error(apiErrorText(e, "Failed to disable"));
    } finally {
      setBusy(false);
    }
  };

  const handleEnable = async (id) => {
    setBusy(true);
    try {
      await apiV2.post(`/admin/gift-cards/${id}/enable`);
      toast.success("Gift card re-enabled");
      mutate();
      mutateStats?.();
      mutateDetail?.();
    } catch (e) {
      toast.error(apiErrorText(e, "Failed to enable"));
    } finally {
      setBusy(false);
    }
  };

  // Resend the gift card link to the recipient (Phase 4.5).
  // Rate-limited 5/day per card on the backend; we surface 429 nicely.
  const handleResend = async (id) => {
    setBusy(true);
    try {
      const { data } = await apiV2.post(`/admin/gift-cards/${id}/resend-email`);
      const channels = ["email", data?.text_sent && (data?.text_channel === "whatsapp" ? "WhatsApp" : "text")].filter(Boolean).join(" + ");
      toast.success(`Gift card resent via ${channels} to ${data?.recipient || "recipient"}`);
    } catch (e) {
      const reason = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.error("Rate limit reached — try again later (5 resends per card per day).");
      } else if (reason === "card_has_no_recipient_email") {
        toast.error("No recipient email or phone on file for this gift card");
      } else {
        toast.error(reason || "Could not resend email");
      }
    } finally {
      setBusy(false);
    }
  };

  // Unified handler for Adjust and Refund (both take amount + optional note)
  const submitActionDialog = async () => {
    if (!actionDialog) return;
    const amount = parseFloat(actionAmount);
    if (!Number.isFinite(amount)) {
      toast.error("Enter a valid number");
      return;
    }
    if (actionDialog.type === "adjust" && amount === 0) {
      toast.error("Adjustment cannot be zero");
      return;
    }
    if (actionDialog.type === "refund" && amount <= 0) {
      toast.error("Refund must be greater than zero");
      return;
    }
    setBusy(true);
    try {
      await apiV2.post(
        `/admin/gift-cards/${actionDialog.cardId}/${actionDialog.type}`,
        { amount, note: actionNote || undefined },
      );
      toast.success(actionDialog.type === "adjust" ? "Adjustment recorded" : "Refund recorded");
      mutate();
      mutateStats?.();
      mutateDetail?.();
      setActionDialog(null);
      setActionAmount("");
      setActionNote("");
    } catch (e) {
      toast.error(apiErrorText(e, `${actionDialog.type} failed`));
    } finally {
      setBusy(false);
    }
  };

  const openAdjust = (cardId) => {
    setActionDialog({ type: "adjust", cardId });
    setActionAmount("");
    setActionNote("");
  };
  const openRefund = (cardId) => {
    setActionDialog({ type: "refund", cardId });
    setActionAmount("");
    setActionNote("");
  };

  // Summary stats — prefer authoritative org-wide stats endpoint.
  // We keep just 3 tiles for an Apple-clean overview: Outstanding (the live
  // liability that matters), Redeemed this month (revenue signal), and
  // Total Issued (cumulative). Card counts are visible via the filter pills
  // so they don't need their own tile.
  const totals = stats
    ? {
        outstanding: stats.outstanding,
        issued: stats.issued_total,
        redeemedThisMonth: stats.redeemed_this_month,
      }
    : cards.reduce(
        (acc, c) => {
          acc.outstanding += Number(c.remaining_balance || 0);
          acc.issued += Number(c.initial_value || 0);
          return acc;
        },
        { outstanding: 0, issued: 0, redeemedThisMonth: 0 },
      );

  // CSV export — server-side, honours current filters so what you download
  // matches what you see. Uses fetch directly (bypasses axios JSON parsing).
  const handleExportCsv = async () => {
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status", statusFilter);
      if (search) qs.set("search", search);
      const resp = await apiV2.get(`/admin/gift-cards/export.csv?${qs.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gift-cards-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(apiErrorText(e, "Could not export CSV"));
    }
  };

  return (
    <TabPageLayout
      title="Gift Cards"
      subtitle="Issue and redeem depleting-balance gift cards"
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={total === 0}
            className="h-9 gap-1.5 hidden sm:inline-flex"
            data-testid="gift-cards-export-csv-btn"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
          <Button
            onClick={() => setShowIssue(true)}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4 rounded-md shadow-[0_8px_24px_rgba(37,99,235,0.18)] hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)] transition-shadow"
            data-testid="gift-cards-issue-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Issue
          </Button>
        </div>
      }
    >
      {/* Compact glance row — one line, no oversized tiles */}
      <div className="card-soft p-4 mb-6 flex flex-wrap items-center gap-x-8 gap-y-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-0.5">Outstanding</p>
          <p className="text-xl font-semibold text-gray-900">{fmtMoney(totals.outstanding, stats?.currency)}</p>
        </div>
        <div className="h-8 w-px bg-gray-200" />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-0.5">Redeemed this month</p>
          <p className="text-xl font-semibold text-gray-900">{fmtMoney(totals.redeemedThisMonth || 0, stats?.currency)}</p>
        </div>
        <div className="h-8 w-px bg-gray-200" />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-0.5">Total issued</p>
          <p className="text-xl font-semibold text-gray-900">{fmtMoney(totals.issued, stats?.currency)}</p>
        </div>
      </div>

      {/* Unified settings — collapsed by default, summary line shows status */}
      <GiftCardSettingsPanel />

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search code or recipient…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-10"
            data-testid="gift-cards-search-input"
          />
        </div>
        <ChipTabs
          value={statusFilter}
          onChange={onStatusChange}
          testIdPrefix="gift-cards-filter"
          tabs={[
            { value: '', label: 'All' },
            { value: 'active', label: 'Active', color: 'green' },
            { value: 'depleted', label: 'Depleted', color: 'gray' },
            { value: 'expired', label: 'Expired', color: 'amber' },
            { value: 'disabled', label: 'Disabled', color: 'red' },
          ]}
        />
      </div>

      {/* Card list */}
      {isLoading ? (
        <PageLoader />
      ) : cards.length === 0 ? (
        <EmptyState
          icon={<Gift />}
          title="No gift cards yet"
          description="Issue your first gift card to start rewarding customers. Codes act as bearer tokens — anyone with the code can redeem the balance."
          action={
            <LayoutActionButton onClick={() => setShowIssue(true)} icon={Plus} data-testid="gift-cards-empty-issue-btn">
              Issue Gift Card
            </LayoutActionButton>
          }
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block card-soft overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3 text-right">Issued</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody>
                {cards.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedId(c.id)}
                    data-testid={`gift-card-row-${c.code}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold text-gray-900">{c.code}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyCode(c.code); }}
                          className="text-gray-400 hover:text-gray-700"
                          title="Copy code"
                        >
                          {copiedCode === c.code ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                        {c.source === "bought" && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200"
                            title="Purchased by a customer via the public gift card page"
                          >
                            <ShoppingBag className="w-2.5 h-2.5" /> Bought
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="flex items-center gap-1.5">
                        {c.recipient_name || c.recipient_email || <span className="text-gray-400">—</span>}
                        {c.recipient_email && <Mail className="w-3 h-3 text-gray-300" title={c.recipient_email} />}
                        {c.recipient_phone && <Smartphone className="w-3 h-3 text-gray-300" title={c.recipient_phone} />}
                      </div>
                      {c.recipient_email && c.recipient_name && (
                        <div className="text-xs text-gray-400">{c.recipient_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold text-gray-900">{fmtMoney(c.remaining_balance, c.currency)}</div>
                      {Number(c.initial_value) > 0 && (
                        <div className="mt-1 ml-auto h-1 w-16 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${Number(c.remaining_balance) <= 0 ? "bg-gray-300" : "bg-emerald-500"}`}
                            style={{ width: `${Math.max(0, Math.min(100, (Number(c.remaining_balance) / Number(c.initial_value)) * 100))}%` }}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">
                      {fmtMoney(c.initial_value, c.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip status={c.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-600">{fmtDate(c.expires_at)}</td>
                    <td className="px-4 py-3 text-gray-400">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {cards.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className="w-full card-soft p-4 text-left active:bg-gray-50 transition-colors"
                data-testid={`gift-card-row-${c.code}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs font-semibold text-gray-900 truncate">{c.code}</span>
                      <span
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); copyCode(c.code); }}
                        className="text-gray-400 active:text-gray-700 p-0.5 cursor-pointer"
                      >
                        {copiedCode === c.code ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </span>
                      {c.source === "bought" && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200">
                          <ShoppingBag className="w-2.5 h-2.5" /> Bought
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {c.recipient_name || c.recipient_email || "No recipient"}
                    </div>
                  </div>
                  <StatusChip status={c.status} className="flex-shrink-0" />
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div>
                    <div className="text-xs text-gray-500">Balance</div>
                    <div className="font-semibold text-gray-900">{fmtMoney(c.remaining_balance, c.currency)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Expires</div>
                    <div className="text-sm text-gray-700">{fmtDate(c.expires_at)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Pagination footer — only shown when we have more than one page */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 px-1" data-testid="gift-cards-pagination">
              <div className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-900">{startRow}–{endRow}</span> of <span className="font-medium text-gray-900">{total}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={!hasPrev || isLoading}
                  data-testid="gift-cards-page-prev"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasNext || isLoading}
                  data-testid="gift-cards-page-next"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Issue modal */}
      <IssueGiftCardDialog
        open={showIssue}
        onOpenChange={setShowIssue}
        onIssued={() => { mutate(); mutateStats?.(); setShowIssue(false); }}
      />

      {/* Detail drawer */}
      <Sheet open={!!selectedId} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5" /> Gift Card Details
            </SheetTitle>
            <SheetDescription>Full ledger and lifecycle actions for this card.</SheetDescription>
          </SheetHeader>

          {!detail ? (
            <PageLoader minHeight="h-40" />
          ) : (
            <div className="mt-6 space-y-6">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-xl p-5">
                <div className="text-xs uppercase tracking-wider opacity-80">Balance</div>
                <div className="text-3xl font-bold mt-1">
                  {fmtMoney(detail.card.remaining_balance, detail.card.currency)}
                </div>
                <div className="text-xs opacity-80 mt-1">
                  of {fmtMoney(detail.card.initial_value, detail.card.currency)} issued
                </div>
                <div className="mt-4 flex items-center justify-between bg-white/10 backdrop-blur rounded-lg px-3 py-2">
                  <span className="font-mono text-sm tracking-wider">{detail.card.code}</span>
                  <button
                    onClick={() => copyCode(detail.card.code)}
                    className="text-white/80 hover:text-white"
                  >
                    {copiedCode === detail.card.code ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <InfoCell label="Status">
                  <StatusChip status={detail.effective_status} />
                </InfoCell>
                <InfoCell label="Expires">{fmtDate(detail.card.expires_at)}</InfoCell>
                <InfoCell label="Recipient">
                  {detail.card.recipient_name || "—"}
                  {detail.card.recipient_email && (
                    <div className="text-xs text-gray-500 mt-0.5">{detail.card.recipient_email}</div>
                  )}
                  {detail.card.recipient_phone && (
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <Smartphone className="w-3 h-3" /> {detail.card.recipient_phone}
                    </div>
                  )}
                </InfoCell>
                <InfoCell label="Purchaser">
                  {detail.card.purchaser_name || "—"}
                  {detail.card.purchaser_email && (
                    <div className="text-xs text-gray-500 mt-0.5">{detail.card.purchaser_email}</div>
                  )}
                </InfoCell>
              </div>

              {detail.card.note && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  <div className="text-xs uppercase tracking-wider text-amber-700 mb-1">Note</div>
                  {detail.card.note}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAdjust(detail.card.id)}
                  disabled={busy || detail.card.status === "disabled"}
                  data-testid="gift-card-adjust-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Adjust
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openRefund(detail.card.id)}
                  disabled={busy || detail.card.status === "disabled"}
                  data-testid="gift-card-refund-btn"
                >
                  <PlusCircle className="w-4 h-4 mr-1.5" /> Refund
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleResend(detail.card.id)}
                  disabled={busy || (!detail.card.recipient_email && !detail.card.recipient_phone)}
                  data-testid="gift-card-resend-email-btn"
                  title={detail.card.recipient_email || detail.card.recipient_phone || "No recipient contact on file"}
                >
                  <Mail className="w-4 h-4 mr-1.5" /> Resend
                </Button>
                {detail.card.status !== "disabled" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 border-red-200"
                    onClick={() => setDisableConfirm(detail.card.id)}
                    disabled={busy}
                    data-testid="gift-card-disable-btn"
                  >
                    <Ban className="w-4 h-4 mr-1.5" /> Disable
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                    onClick={() => handleEnable(detail.card.id)}
                    disabled={busy}
                    data-testid="gift-card-enable-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Re-enable
                  </Button>
                )}
              </div>

              {/* Ledger */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Ledger</h3>
                <div className="space-y-2">
                  {detail.transactions.map((t) => {
                    const meta = TXN_LABEL[t.txn_type] || TXN_LABEL.adjust;
                    const Icon = meta.Icon;
                    // Build a context line for redeem rows: prefer the booking
                    // service name + customer; fall back to operator note.
                    const b = t.booking;
                    let contextLine = null;
                    if (t.txn_type === "redeem" && b) {
                      const parts = [];
                      if (b.service_name) parts.push(b.service_name);
                      if (b.customer_name) parts.push(b.customer_name);
                      if (b.confirmation_code) parts.push(`#${b.confirmation_code}`);
                      if (parts.length) contextLine = parts.join(" · ");
                    }
                    if (!contextLine && t.note) contextLine = t.note;
                    return (
                      <div
                        key={t.id}
                        className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`${meta.color}`}><Icon className="w-4 h-4" /></div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900">{meta.label}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {fmtDate(t.created_at)}{contextLine ? ` · ${contextLine}` : ""}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-semibold ${Number(t.amount) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {Number(t.amount) >= 0 ? "+" : ""}{fmtMoney(t.amount, t.currency)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Balance: {fmtMoney(t.balance_after, t.currency)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Adjust / Refund Dialog (replaces window.prompt) */}
      <Dialog
        open={!!actionDialog}
        onOpenChange={(o) => { if (!o) { setActionDialog(null); setActionAmount(""); setActionNote(""); } }}
      >
        <DialogContent className="sm:max-w-sm" data-testid="gift-card-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionDialog?.type === "adjust" ? (
                <><RefreshCw className="w-5 h-5 text-blue-600" /> Adjust Balance</>
              ) : (
                <><PlusCircle className="w-5 h-5 text-emerald-600" /> Refund to Card</>
              )}
            </DialogTitle>
            <DialogDescription>
              {actionDialog?.type === "adjust"
                ? "Add or remove balance. Use a negative number to debit (e.g. −10), positive to credit (e.g. 25)."
                : "Add balance back to the card. Use this when a booking is cancelled and you want to preserve the customer's value."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="action-amount" className="text-sm">Amount</Label>
              <Input
                id="action-amount"
                type="number"
                step="0.01"
                value={actionAmount}
                onChange={(e) => setActionAmount(e.target.value)}
                placeholder={actionDialog?.type === "adjust" ? "-10.00 or 25.00" : "20.00"}
                onKeyDown={(e) => { if (e.key === "Enter") submitActionDialog(); }}
                data-testid="gift-card-action-amount-input"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="action-note" className="text-sm">Note <span className="text-gray-400">(optional)</span></Label>
              <Textarea
                id="action-note"
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder={actionDialog?.type === "adjust" ? "Reason for adjustment…" : "Booking ID or reason…"}
                rows={2}
                data-testid="gift-card-action-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={submitActionDialog}
              disabled={busy || !actionAmount}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="gift-card-action-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable confirmation (replaces window.confirm) */}
      <AlertDialog open={!!disableConfirm} onOpenChange={(o) => !o && setDisableConfirm(null)}>
        <AlertDialogContent data-testid="gift-card-disable-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-600" />
              Disable this gift card?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The remaining balance will become unusable. You can re-enable it later if needed (e.g. if a customer reports a card lost-then-found).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDisable(disableConfirm)}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="gift-card-disable-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Disable Card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TabPageLayout>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────────────

const InfoCell = ({ label, children }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">{label}</div>
    <div className="text-gray-900">{children}</div>
  </div>
);

const IssueGiftCardDialog = ({ open, onOpenChange, onIssued }) => {
  // UAE orgs deliver texts over WhatsApp; USA orgs over SMS
  const isUAE = (localStorage.getItem("orgCurrency") || "USD").toUpperCase() === "AED";
  const textChannelLabel = isUAE ? "WhatsApp" : "text message";
  const [form, setForm] = useState({
    initial_value: "",
    expires_at: defaultExpiry(),
    recipient_name: "",
    recipient_email: "",
    recipient_phone: "",
    purchaser_name: "",
    purchaser_email: "",
    note: "",
    send_email: true,
    send_text: true,
  });
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setForm({
      initial_value: "",
      expires_at: defaultExpiry(),
      recipient_name: "",
      recipient_email: "",
      recipient_phone: "",
      purchaser_name: "",
      purchaser_email: "",
      note: "",
      send_email: true,
      send_text: true,
    });
    setIssued(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = parseFloat(form.initial_value);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    if (!form.expires_at) {
      toast.error("Expiration date is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        initial_value: value,
        // Send the date string as-is — backend interprets it as end-of-day in
        // the ORG's timezone (not the operator's browser timezone). This means
        // "Dec 31" means "Dec 31 close-of-business" no matter where the agent
        // is sitting or where the server lives.
        expires_at: form.expires_at,
        send_email: !!form.send_email,
        send_text: !!form.send_text,
      };
      ["recipient_name", "recipient_email", "recipient_phone", "purchaser_name", "purchaser_email", "note"]
        .forEach((k) => { if (form[k]) payload[k] = form[k]; });

      const { data } = await apiV2.post("/admin/gift-cards", payload);
      setIssued(data);
      hapticSuccess();
      toast.success(`Gift card issued: ${data.code}`);
      onIssued?.();
    } catch (e) {
      toast.error(apiErrorText(e, "Failed to issue gift card"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-lg" data-testid="gift-card-issue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5" /> {issued ? "Gift Card Issued" : "Issue Gift Card"}
          </DialogTitle>
          <DialogDescription>
            {issued
              ? "Share this code with your customer. The code is the bearer token — anyone with it can redeem."
              : "Codes are 16-character bearer tokens. Currency defaults to your organization currency."}
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          <div className="space-y-4 py-2">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-xl p-6 text-center">
              <div className="text-xs uppercase tracking-widest opacity-70">Balance</div>
              <div className="text-4xl font-bold mt-1">
                {fmtMoney(issued.initial_value, issued.currency)}
              </div>
              <div className="mt-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2 font-mono text-lg tracking-wider">
                {issued.code}
                <button
                  onClick={() => { navigator.clipboard.writeText(issued.code); toast.success("Copied"); }}
                  className="text-white/80 hover:text-white"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs opacity-70 mt-3">Expires {fmtDate(issued.expires_at)}</div>
            </div>
            {form.send_email && form.recipient_email && (
              <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="gift-card-email-queued-chip">
                <Mail className="w-4 h-4" />
                Email queued to {form.recipient_email}
              </div>
            )}
            {form.send_text && form.recipient_phone && (
              <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="gift-card-text-queued-chip">
                {isUAE ? <MessageCircle className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                {isUAE ? "WhatsApp" : "Text"} queued to {form.recipient_phone}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={reset} data-testid="gift-card-issue-another-btn">
                Issue another
              </Button>
              <Button onClick={() => { reset(); onOpenChange(false); }} data-testid="gift-card-issue-done-btn">
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="gc-value">Amount</Label>
                <Input
                  id="gc-value"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="100.00"
                  value={form.initial_value}
                  onChange={(e) => update("initial_value", e.target.value)}
                  required
                  data-testid="gift-card-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="gc-expires">Expires</Label>
                <Input
                  id="gc-expires"
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => update("expires_at", e.target.value)}
                  required
                  min={new Date().toISOString().slice(0, 10)}
                  data-testid="gift-card-expires-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="gc-rname">Recipient name <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  id="gc-rname"
                  value={form.recipient_name}
                  onChange={(e) => update("recipient_name", e.target.value)}
                  placeholder="Jane Doe"
                  data-testid="gift-card-recipient-name-input"
                />
              </div>
              <div>
                <Label htmlFor="gc-remail">Recipient email <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  id="gc-remail"
                  type="email"
                  value={form.recipient_email}
                  onChange={(e) => update("recipient_email", e.target.value)}
                  placeholder="jane@example.com"
                  data-testid="gift-card-recipient-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="gc-rphone">
                Recipient phone <span className="text-gray-400 font-normal">(optional — for {textChannelLabel} delivery)</span>
              </Label>
              <Input
                id="gc-rphone"
                type="tel"
                value={form.recipient_phone}
                onChange={(e) => update("recipient_phone", e.target.value)}
                placeholder={isUAE ? "+971 50 123 4567" : "+1 (303) 555-0123"}
                data-testid="gift-card-recipient-phone-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="gc-pname">From <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  id="gc-pname"
                  value={form.purchaser_name}
                  onChange={(e) => update("purchaser_name", e.target.value)}
                  placeholder="Your name or business"
                  data-testid="gift-card-purchaser-name-input"
                />
              </div>
              <div>
                <Label htmlFor="gc-note-quick">Personal message <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  id="gc-note-quick"
                  value={form.note}
                  onChange={(e) => update("note", e.target.value)}
                  placeholder="Happy birthday!"
                  data-testid="gift-card-note-input"
                />
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Send the code to the recipient</p>
              <label className="flex items-center gap-2 text-sm text-gray-700 select-none py-1 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-blue-600"
                  checked={form.send_email}
                  onChange={(e) => update("send_email", e.target.checked)}
                  disabled={!form.recipient_email}
                  data-testid="gift-card-send-email-checkbox"
                />
                <Mail className="w-4 h-4 text-gray-400" />
                Email {!form.recipient_email && <span className="text-xs text-gray-400">(needs recipient email)</span>}
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 select-none py-1 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-blue-600"
                  checked={form.send_text}
                  onChange={(e) => update("send_text", e.target.checked)}
                  disabled={!form.recipient_phone}
                  data-testid="gift-card-send-text-checkbox"
                />
                {isUAE ? <MessageCircle className="w-4 h-4 text-gray-400" /> : <Smartphone className="w-4 h-4 text-gray-400" />}
                {isUAE ? "WhatsApp" : "Text message (SMS)"} {!form.recipient_phone && <span className="text-xs text-gray-400">(needs recipient phone)</span>}
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !(parseFloat(form.initial_value) > 0) || !form.expires_at} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="gift-card-submit-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Issue Gift Card
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GiftCardsTab;


// =============================================================================
// GiftCardSettingsPanel — Unified collapsible panel combining:
//   - Public gift card purchases (Phase 4)
//   - Lifecycle email notifications  (Phase 4.5)
//
// Apple-style: collapsed by default with a one-line status summary. Operators
// see their cards first, not configuration. Single click to manage everything.
// =============================================================================

function GiftCardSettingsPanel() {
  const [expanded, setExpanded] = useState(false);

  const { data: pub, mutate: mutatePub, isLoading: pubLoading } = useSWR(
    "/admin/gift-cards/settings/public-purchases",
    swrFetcher,
    { revalidateOnFocus: false }
  );
  const { data: lifecycle, mutate: mutateLifecycle, isLoading: lcLoading } = useSWR(
    "/admin/gift-cards/settings/lifecycle-emails",
    swrFetcher,
    { revalidateOnFocus: false }
  );

  // Don't flash an empty card on first load — wait for both
  if (pubLoading || lcLoading || !pub || !lifecycle) {
    return (
      <div className="card-soft p-4 mb-6 h-14 animate-pulse" />
    );
  }

  const lifecycleOn = ["email_on_redeem", "email_on_depleted", "email_on_adjust", "email_on_expiring"]
    .filter((k) => !!lifecycle[k]).length;

  return (
    <div
      className="card-soft mb-6 overflow-hidden"
      data-testid="gift-card-settings-panel"
    >
      {/* Summary header — clickable to toggle expansion */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 hover:bg-gray-50/60 transition-colors text-left"
        data-testid="gift-card-settings-toggle"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3 min-w-0">
          <SettingsIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">Settings</div>
            <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${pub.purchases_enabled ? "bg-emerald-500" : "bg-gray-300"}`} />
                Public sales · <span className="font-medium text-gray-700">{pub.purchases_enabled ? "Live" : "Off"}</span>
              </span>
              <span className="text-gray-300">·</span>
              <span>
                Notifications · <span className="font-medium text-gray-700">{lifecycleOn} of 4 on</span>
              </span>
            </div>
          </div>
        </div>
        <span className="text-gray-400 flex-shrink-0">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          <PublicPurchaseSection settings={pub} mutate={mutatePub} />
          <LifecycleEmailSection settings={lifecycle} mutate={mutateLifecycle} />
        </div>
      )}
    </div>
  );
}

function PublicPurchaseSection({ settings, mutate }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftAmounts, setDraftAmounts] = useState("");
  const [draftMin, setDraftMin] = useState("");
  const [draftMax, setDraftMax] = useState("");
  const [draftExpiry, setDraftExpiry] = useState("");
  const [copyHint, setCopyHint] = useState(false);

  const startEdit = () => {
    setDraftAmounts((settings.suggested_amounts || []).join(", "));
    setDraftMin(String(settings.min_amount));
    setDraftMax(String(settings.max_amount));
    setDraftExpiry(String(settings.default_expiry_months));
    setEditing(true);
  };

  const toggleEnabled = async () => {
    setSaving(true);
    try {
      await apiV2.patch("/admin/gift-cards/settings/public-purchases", {
        purchases_enabled: !settings.purchases_enabled,
      });
      toast.success(settings.purchases_enabled ? "Public sales paused" : "Public sales live");
      mutate();
    } catch (e) {
      toast.error(apiErrorText(e, "Could not update settings"));
    } finally {
      setSaving(false);
    }
  };

  const saveAmounts = async () => {
    const parsed = draftAmounts.split(",").map((s) => parseFloat(s.trim())).filter((n) => Number.isFinite(n) && n > 0);
    if (parsed.length === 0 || parsed.length > 8) {
      toast.error("Enter 1-8 positive amounts separated by commas");
      return;
    }
    const minVal = parseFloat(draftMin);
    const maxVal = parseFloat(draftMax);
    const months = parseInt(draftExpiry, 10);
    if (!Number.isFinite(minVal) || !Number.isFinite(maxVal) || minVal <= 0 || maxVal <= 0) {
      toast.error("Min/max must be positive");
      return;
    }
    if (minVal > maxVal) {
      toast.error("Min cannot exceed max");
      return;
    }
    if (!Number.isFinite(months) || months < 1 || months > 60) {
      toast.error("Expiry months must be between 1 and 60");
      return;
    }
    setSaving(true);
    try {
      await apiV2.patch("/admin/gift-cards/settings/public-purchases", {
        suggested_amounts: parsed,
        min_amount: minVal,
        max_amount: maxVal,
        default_expiry_months: months,
      });
      toast.success("Settings saved");
      setEditing(false);
      mutate();
    } catch (e) {
      toast.error(apiErrorText(e, "Could not save settings"));
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(settings.public_url);
    setCopyHint(true);
    toast.success("Link copied");
    setTimeout(() => setCopyHint(false), 1500);
  };

  return (
    <div className="p-5" data-testid="gift-card-public-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Gift className="w-4 h-4 text-blue-600" /> Public gift card sales
          </h4>
          <p className="text-xs text-gray-500 mt-1">Let customers buy gift cards for friends and family directly from a public link.</p>
        </div>
        <Button
          onClick={toggleEnabled}
          disabled={saving}
          size="sm"
          variant={settings.purchases_enabled ? "outline" : "default"}
          className={settings.purchases_enabled ? "" : "bg-blue-600 hover:bg-blue-700 text-white"}
          data-testid="gift-card-public-toggle"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (settings.purchases_enabled ? "Turn off" : "Turn on")}
        </Button>
      </div>

      {settings.purchases_enabled && (
        <div className="mt-3">
          <Label className="text-[11px] uppercase tracking-wide text-gray-500">Share this link</Label>
          <div className="mt-1.5 flex items-center gap-2">
            <Input value={settings.public_url} readOnly className="font-mono text-xs h-9" data-testid="gift-card-public-url" />
            <Button onClick={copyUrl} size="sm" variant="outline" data-testid="gift-card-public-url-copy">
              {copyHint ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100">
        {!editing ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <SettingPill label="Suggested" value={(settings.suggested_amounts || []).map((a) => fmtMoney(a, settings.currency)).join(" · ")} />
            <SettingPill label="Min" value={fmtMoney(settings.min_amount, settings.currency)} />
            <SettingPill label="Max" value={fmtMoney(settings.max_amount, settings.currency)} />
            <SettingPill label="Expires after" value={`${settings.default_expiry_months} mo`} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs text-gray-600">Suggested amounts (comma-separated)</Label>
              <Input value={draftAmounts} onChange={(e) => setDraftAmounts(e.target.value)} placeholder="25, 50, 100, 200" data-testid="gift-card-suggested-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Min amount ({settings.currency})</Label>
              <Input type="number" min="1" value={draftMin} onChange={(e) => setDraftMin(e.target.value)} data-testid="gift-card-min-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Max amount ({settings.currency})</Label>
              <Input type="number" min="1" value={draftMax} onChange={(e) => setDraftMax(e.target.value)} data-testid="gift-card-max-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Default expiry (months, 1-60)</Label>
              <Input type="number" min="1" max="60" value={draftExpiry} onChange={(e) => setDraftExpiry(e.target.value)} data-testid="gift-card-expiry-input" />
            </div>
          </div>
        )}
        <div className="mt-3 flex justify-end gap-2">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
              <Button size="sm" onClick={saveAmounts} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="gift-card-settings-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Save
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={startEdit} data-testid="gift-card-settings-edit">Edit defaults</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingPill({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">{label}</div>
      <div className="text-sm font-semibold text-gray-900 truncate" title={value}>{value || "—"}</div>
    </div>
  );
}

function LifecycleEmailSection({ settings, mutate }) {
  const [saving, setSaving] = useState(false);

  const toggle = async (field) => {
    setSaving(true);
    const next = { ...settings, [field]: !settings[field] };
    try {
      await apiV2.patch("/admin/gift-cards/settings/lifecycle-emails", next);
      toast.success(next[field] ? "Email enabled" : "Email disabled");
      mutate();
    } catch (e) {
      toast.error(apiErrorText(e, "Could not update"));
    } finally {
      setSaving(false);
    }
  };

  const events = [
    { key: "email_on_redeem",   label: "When card is used at checkout",  hint: "Helps recipients spot unauthorized use" },
    { key: "email_on_depleted", label: "When balance reaches $0",        hint: "Friendly closing note + upsell" },
    { key: "email_on_adjust",   label: "When you adjust the balance",    hint: "Transparency for operator-led changes" },
    { key: "email_on_expiring", label: "30 / 7 / 1 days before expiry",  hint: "Recovers unused balance" },
  ];

  return (
    <div className="p-5" data-testid="gift-card-lifecycle-emails">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-4 h-4 text-blue-600" /> Lifecycle notifications
        </h4>
        <p className="text-xs text-gray-500 mt-1">
          Automatic emails to gift card recipients. Capped at 10/hour per recipient to prevent abuse.
        </p>
      </div>
      <div className="space-y-1">
        {events.map((e) => {
          const enabled = !!settings[e.key];
          return (
            <label
              key={e.key}
              className="flex items-start gap-3 py-2 px-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              data-testid={`gift-card-toggle-${e.key}`}
            >
              <input
                type="checkbox"
                checked={enabled}
                onChange={() => toggle(e.key)}
                disabled={saving}
                className="mt-1 w-4 h-4 rounded text-blue-600 cursor-pointer"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">{e.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{e.hint}</div>
              </div>
              <span className={`text-xs font-semibold ${enabled ? "text-emerald-600" : "text-gray-400"}`}>
                {enabled ? "On" : "Off"}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
